// src\components\admin\AdminHeader.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  FiMenu,
  FiChevronLeft,
  FiBell,
  FiUser,
  FiChevronDown,
  FiLogOut,
  FiMail,
  FiPackage,
  FiCalendar,
  FiBriefcase,
} from 'react-icons/fi';
import {
  FaHeart,
  FaRegFaceGrinStars,
  FaRegComment,
  FaReply,
} from 'react-icons/fa6';
import { AiOutlineShareAlt } from 'react-icons/ai';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  doc,
  getDoc,
  updateDoc,
  onSnapshot,
  collection,
  query,
  orderBy,
  limit,
  writeBatch,
  where,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useNavigate, NavLink } from 'react-router-dom';

export default function AdminHeader({
  onMenuClick,
  isSidebarCollapsed,
  onToggleSidebar,
}) {
  const [userData, setUserData] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const { currentUser, logout, isAdmin, getIdToken } = useAuth();
  const { actualTheme } = useTheme();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  const isDark = actualTheme === 'dark';

  // Fetch user data
  useEffect(() => {
    if (!currentUser?.uid) return;
    const fetchUserData = async () => {
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) setUserData(userDoc.data());
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    fetchUserData();
  }, [currentUser]);

  // Set up real-time notifications listener from notifications collection
  useEffect(() => {
    if (!currentUser?.uid || !isAdmin) {
      return;
    }

    setLoadingNotifications(true);

    const notificationsQuery = query(
      collection(db, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(100),
    );

    const unsubscribe = onSnapshot(
      notificationsQuery,
      snapshot => {
        const notificationList = [];
        let unread = 0;

        snapshot.forEach(doc => {
          const data = doc.data();
          notificationList.push({
            id: doc.id,
            ...data,
          });
          if (!data.read) {
            unread++;
          }
        });

        setNotifications(notificationList);
        setUnreadCount(unread);
        setLoadingNotifications(false);
      },
      error => {
        console.error('Error fetching notifications:', error);
        setLoadingNotifications(false);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [currentUser?.uid, isAdmin]);

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target))
        setIsDropdownOpen(false);
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      )
        setShowNotifications(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markNotificationAsRead = async notificationId => {
    if (!currentUser?.uid) return;

    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
        readAt: new Date().toISOString(),
        readBy: currentUser.uid,
      });

      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, read: true } : notif,
        ),
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!currentUser?.uid || notifications.length === 0) return;

    try {
      const batch = writeBatch(db);
      const unreadNotifications = notifications.filter(n => !n.read);

      unreadNotifications.forEach(notif => {
        const notifRef = doc(db, 'notifications', notif.id);
        batch.update(notifRef, {
          read: true,
          readAt: new Date().toISOString(),
          readBy: currentUser.uid,
        });
      });

      await batch.commit();

      setUnreadCount(0);
      setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = notification => {
    // Mark as read if unread
    if (!notification.read) {
      markNotificationAsRead(notification.id);
    }

    // Close notification panel
    setShowNotifications(false);

    // Navigate based on notification type
    if (
      notification.type === 'blog-comment' ||
      notification.type === 'blog-reply' ||
      notification.type === 'blog-reaction'
    ) {
      const blogId = notification.data?.blogId;
      if (blogId) {
        navigate(
          `/admin/blog?filter=${blogId}&notification=${notification.id}`,
        );
      } else {
        navigate(notification.link || '/admin/dashboard');
      }
    } else {
      navigate(notification.link || '/admin/dashboard');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/wdalogin');
  };

  const getNotificationIcon = type => {
    switch (type) {
      case 'ready-made-inquiry':
        return <FiPackage className="w-4 h-4 text-cyan-500" />;
      case 'subscription-inquiry':
        return <FiCalendar className="w-4 h-4 text-purple-500" />;
      case 'hire-inquiry':
        return <FiBriefcase className="w-4 h-4 text-emerald-500" />;
      case 'contact-message':
        return <FiMail className="w-4 h-4 text-blue-500" />;
      case 'review':
        return <FaRegFaceGrinStars className="w-4 h-4 text-yellow-500" />;
      case 'portfolio-like':
        return <FaHeart className="w-4 h-4 text-red-500" />;
      case 'portfolio-share':
        return <AiOutlineShareAlt className="w-4 h-4 text-green-500" />;
      case 'blog-comment':
        return <FaRegComment className="w-4 h-4 text-indigo-500" />;
      case 'blog-reply':
        return <FaReply className="w-4 h-4 text-orange-500" />;
      case 'blog-reaction':
        return <FaHeart className="w-4 h-4 text-pink-500" />;
      case 'blog-update':
        return <FiBell className="w-4 h-4 text-emerald-500" />;
      default:
        return <FiBell className="w-4 h-4" style={{ color: 'var(--muted)' }} />;
    }
  };

  const formatRelativeTime = timestamp => {
    if (!timestamp) return 'Recently';
    try {
      let date;
      if (timestamp?.toDate) {
        date = timestamp.toDate();
      } else if (timestamp?.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        date = new Date(timestamp);
      }

      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} min ago`;
      if (diffHours < 24)
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      return date.toLocaleDateString();
    } catch (error) {
      return 'Recently';
    }
  };

  // Helper to check if notification should show image
  const shouldShowImage = notif => {
    const imageTypes = [
      'blog-comment',
      'blog-reply',
      'blog-reaction',
      'blog-update',
      'portfolio-like',
      'portfolio-share',
      'ready-made-inquiry',
      'subscription-inquiry',
    ];
    return imageTypes.includes(notif.type) && notif.imageUrl;
  };

  return (
    <div
      className="h-16 flex items-center justify-between px-6 border-b backdrop-blur-md"
      style={{
        backgroundColor: 'var(--bg)',
        borderColor: 'var(--border)',
      }}
    >
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-lg transition-colors hover:bg-black/5 dark:hover:bg-white/5"
        >
          <FiMenu className="w-5 h-5" style={{ color: 'var(--text)' }} />
        </button>
        <button
          onClick={onToggleSidebar}
          className="hidden lg:block p-2 rounded-lg transition-colors hover:bg-black/5 dark:hover:bg-white/5"
        >
          <FiChevronLeft
            className={`w-5 h-5 transition-transform duration-300 ${
              isSidebarCollapsed ? 'rotate-180' : ''
            }`}
            style={{ color: 'var(--text)' }}
          />
        </button>
        <div className="hidden sm:block">
          <h2 className="text-sm font-medium" style={{ color: 'var(--text)' }}>
            Admin Panel
          </h2>
          <p className="text-xs" style={{ color: 'var(--muted)' }}>
            Manage your portfolio and inquiries
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Notifications */}
        <div className="relative" ref={notificationRef}>
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 rounded-lg transition-colors hover:bg-black/5 dark:hover:bg-white/5"
          >
            <FiBell className="w-5 h-5" style={{ color: 'var(--text)' }} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div
              className="absolute right-0 mt-2 w-96 rounded-xl shadow-lg overflow-hidden border z-50 animate-fade-in-up"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              {/* Header */}
              <div
                className="p-3 border-b flex justify-between items-center"
                style={{ borderColor: 'var(--border)' }}
              >
                <h3 className="font-semibold" style={{ color: 'var(--text)' }}>
                  Notifications ({unreadCount} unread)
                </h3>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs hover:underline"
                    style={{ color: 'var(--muted)' }}
                  >
                    Mark all as read
                  </button>
                )}
              </div>

              {/* Notifications List */}
              <div className="max-h-96 overflow-y-auto">
                {loadingNotifications ? (
                  <div
                    className="p-8 text-center"
                    style={{ color: 'var(--muted)' }}
                  >
                    <div
                      className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin mx-auto"
                      style={{ borderColor: 'var(--text)' }}
                    />
                    <p className="text-xs mt-2">Loading notifications...</p>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="p-8 text-center">
                    <FiBell
                      className="w-10 h-10 mx-auto mb-3 opacity-30"
                      style={{ color: 'var(--text)' }}
                    />
                    <p className="text-sm" style={{ color: 'var(--muted)' }}>
                      No notifications yet
                    </p>
                    <p
                      className="text-xs mt-1"
                      style={{ color: 'var(--muted)' }}
                    >
                      New notifications will appear here
                    </p>
                  </div>
                ) : (
                  notifications.map(notif => (
                    <div
                      key={notif.id}
                      onClick={() => handleNotificationClick(notif)}
                      className={`block p-3 border-b transition-colors hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer ${
                        !notif.read ? 'border-l-4 border-l-blue-500' : ''
                      }`}
                      style={{ borderColor: 'var(--border)' }}
                    >
                      <div className="flex items-start gap-3">
                        {/* Show image for all notification types that have images */}
                        {shouldShowImage(notif) ? (
                          <img
                            src={notif.imageUrl}
                            alt={
                              notif.data?.packageName ||
                              notif.data?.blogTitle ||
                              notif.data?.projectTitle ||
                              'Notification'
                            }
                            className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                            onError={e => {
                              // Fallback to icon if image fails to load
                              e.target.style.display = 'none';
                              const parent = e.target.parentElement;
                              const iconDiv = document.createElement('div');
                              iconDiv.className = 'flex-shrink-0 mt-0.5';
                              // Re-render with icon
                              parent.innerHTML = `<div class="flex-shrink-0 mt-0.5">${getNotificationIcon(notif.type)}</div>`;
                            }}
                          />
                        ) : (
                          <div className="flex-shrink-0 mt-0.5">
                            {getNotificationIcon(notif.type)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p
                              className="text-sm font-medium truncate"
                              style={{ color: 'var(--text)' }}
                            >
                              {notif.title}
                            </p>
                            <span
                              className="text-xs flex-shrink-0"
                              style={{ color: 'var(--muted)' }}
                            >
                              {formatRelativeTime(notif.createdAt)}
                            </span>
                          </div>
                          <p
                            className="text-xs mt-0.5 line-clamp-2"
                            style={{ color: 'var(--muted)' }}
                          >
                            {notif.message}
                          </p>
                          {!notif.read && (
                            <div className="flex items-center gap-1 mt-1">
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                              <span
                                className="text-xs"
                                style={{ color: '#3b82f6' }}
                              >
                                New
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Footer */}
              {notifications.length > 0 && (
                <div
                  className="p-2 border-t text-center"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <button
                    onClick={() => setShowNotifications(false)}
                    className="text-xs hover:underline block w-full py-1"
                    style={{ color: 'var(--muted)' }}
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-3 px-3 py-1.5 rounded-lg transition-colors hover:bg-black/5 dark:hover:bg-white/5"
          >
            {userData?.profilePictureURL ? (
              <img
                src={userData.profilePictureURL}
                alt="Profile"
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <FiUser className="w-4 h-4" style={{ color: 'var(--text)' }} />
              </div>
            )}
            <div className="hidden sm:block text-left">
              <p
                className="text-sm font-medium"
                style={{ color: 'var(--text)' }}
              >
                {userData?.fullName?.split(' ')[0] || 'Admin'}
              </p>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>
                Administrator
              </p>
            </div>
            <FiChevronDown
              className="w-4 h-4 hidden sm:block"
              style={{ color: 'var(--muted)' }}
            />
          </button>

          {isDropdownOpen && (
            <div
              className="absolute right-0 mt-2 w-56 rounded-xl shadow-lg overflow-hidden border z-50 animate-fade-in-up"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              <NavLink
                to="/admin/settings"
                className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-black/5 dark:hover:bg-white/5"
                style={{ color: 'var(--text)' }}
                onClick={() => setIsDropdownOpen(false)}
              >
                <FiUser className="w-4 h-4" />
                <span className="text-sm">Profile Settings</span>
              </NavLink>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 transition-colors hover:bg-black/5 dark:hover:bg-white/5 text-left"
                style={{ color: 'var(--text)' }}
              >
                <FiLogOut className="w-4 h-4" />
                <span className="text-sm">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
